import { useFleetioPartsSearchQuery, useFleetioPartInventoryQuery } from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import CreatableSelect from 'react-select/creatable'
import Spinner from '@/gf/components/Spinner'
import useDebounce from '@/gf/hooks/useDebounce'
import { useMemo, useState } from 'react'

type PartOption = {
  label: string
  value: string
}

const FleetioPartNumberSearch = ({
  value,
  onChange,
  disabled,
  showInventory = true,
}: {
  value: string
  onChange: (partNumber: string) => void
  disabled?: boolean
  showInventory?: boolean
}) => {
  const client = useGqlClient()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)
  const [selectedPartNumber, setSelectedPartNumber] = useState<string | null>(null)
  const [isCustomPartNumber, setIsCustomPartNumber] = useState(false)

  const { data, loading } = useFleetioPartsSearchQuery({
    client,
    variables: { partNumber: debouncedSearch },
    skip: debouncedSearch.length < 2,
  })

  const { data: inventoryData, loading: inventoryLoading } = useFleetioPartInventoryQuery({
    client,
    variables: { partNumber: selectedPartNumber ?? '' },
    skip: !selectedPartNumber || isCustomPartNumber || !showInventory,
  })

  const options: PartOption[] = useMemo(
    () =>
      debouncedSearch.length < 2
        ? []
        : (data?.fleetioPartsSearch ?? []).map((p) => ({ label: p.number, value: p.number })),
    [data?.fleetioPartsSearch, debouncedSearch.length]
  )

  const selectedOption = value ? { label: value, value } : null

  const handleChange = (option: (PartOption & { __isNew__?: boolean }) | null) => {
    const newValue = option?.value ?? ''
    onChange(newValue)
    setSelectedPartNumber(newValue || null)
    setIsCustomPartNumber(option?.__isNew__ ?? false)
  }

  const inventory = inventoryData?.fleetioPartInventory

  return (
    <div className="space-y-2">
      <CreatableSelect
        value={selectedOption}
        onChange={handleChange}
        options={options}
        isLoading={loading}
        placeholder="Search Fleetio parts..."
        className="text-sm w-full"
        onInputChange={setSearch}
        filterOption={() => true}
        isDisabled={disabled}
        isClearable
        formatCreateLabel={(inputValue) => `Create part "${inputValue}"`}
        noOptionsMessage={({ inputValue }) =>
          inputValue.length < 2
            ? 'Type at least 2 characters to search'
            : `No parts found for "${inputValue}"`
        }
      />

      {showInventory && selectedPartNumber && !isCustomPartNumber && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <div className="text-xs font-medium text-blue-800 mb-2">Fleetio Inventory</div>
          {inventoryLoading ? (
            <div className="flex items-center gap-2 text-sm text-blue-600">
              <Spinner size="xs" />
              <span>Loading inventory...</span>
            </div>
          ) : inventory?.locations && inventory.locations.length > 0 ? (
            <div className="space-y-1">
              {inventory.locations
                .filter(
                  (loc) =>
                    loc.name === 'Mt Carmel' ||
                    loc.name === 'Columbus' ||
                    loc.name === 'Indy Warehouse' ||
                    loc.name === '479' ||
                    loc.name === '444'
                )
                .map((loc) => (
                  <div
                    key={loc.name}
                    className="flex justify-between items-center text-sm text-gray-700"
                  >
                    <span className="text-gray-600">{loc.name}</span>
                    <span className="font-medium">{loc.availableQuantity}</span>
                  </div>
                ))}
            </div>
          ) : (
            <div className="text-sm text-gray-500">No inventory found for this part</div>
          )}
        </div>
      )}
    </div>
  )
}

export default FleetioPartNumberSearch
